import React, { useState } from "react";
import {
    useGetStripePaymentStatsQuery,
    useGetStripePaymentListQuery,
} from "../api";

const statusBadge = (status: string): string => {
    if (status === "paid") return "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300";
    if (status === "pending") return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300";
    if (status === "failed") return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300";
    return "bg-gray-100 text-gray-800";
};

const formatMoney = (value: unknown): string => {
    const n = Number(value);
    return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

const sourceLabel = (source: string): string => {
    if (source === "hub") return "Хаб";
    if (source === "game") return "Игра";
    return source;
};

const StripeStatsPage: React.FC = () => {
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [status, setStatus] = useState("");
    const [source, setSource] = useState("");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState("date_time");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

    const statsParams = {
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        source: source || undefined,
    };

    const {
        data: statsData,
        isLoading: isLoadingStats,
        isError: isStatsError,
        error: statsError,
        refetch: refetchStats,
    } = useGetStripePaymentStatsQuery(statsParams, { pollingInterval: 60000 });

    const {
        data: listData,
        isLoading: isLoadingList,
        refetch: refetchList,
    } = useGetStripePaymentListQuery(
        {
            status: status || undefined,
            source: source || undefined,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
            search: search || undefined,
            per_page: 20,
            page,
            sort_by: sortBy,
            sort_order: sortOrder,
        },
        { pollingInterval: 60000 }
    );

    const payments = listData?.data || [];
    const meta = listData?.meta;
    const paid = statsData?.data?.paid ?? { count: 0, total_amount: 0 };
    const failed = statsData?.data?.failed ?? { count: 0, total_amount: 0 };
    const pending = statsData?.data?.pending ?? { count: 0, total_amount: 0 };
    const bySource = statsData?.data?.by_source ?? {};

    const refreshAll = () => {
        refetchStats();
        refetchList();
    };

    const inputClass =
        "border rounded px-3 py-1 text-sm bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100";

    return (
        <div className="space-y-6 max-w-full">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold mb-1 dark:text-gray-200">
                        Статистика Stripe
                    </h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Пополнения ALNY через Stripe (карта). Не путать с Adam Proxy.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={refreshAll}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                    Обновить
                </button>
            </div>

            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    setPage(1);
                    refreshAll();
                }}
                className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 flex flex-wrap gap-4 items-end"
            >
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Дата с
                    </label>
                    <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className={inputClass}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Дата по
                    </label>
                    <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        className={inputClass}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Статус
                    </label>
                    <select
                        value={status}
                        onChange={(e) => {
                            setStatus(e.target.value);
                            setPage(1);
                        }}
                        className={inputClass}
                    >
                        <option value="">Все</option>
                        <option value="paid">Paid</option>
                        <option value="pending">Pending</option>
                        <option value="failed">Failed</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Источник
                    </label>
                    <select
                        value={source}
                        onChange={(e) => {
                            setSource(e.target.value);
                            setPage(1);
                        }}
                        className={inputClass}
                    >
                        <option value="">Все</option>
                        <option value="hub">Хаб</option>
                        <option value="game">Игра</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Поиск
                    </label>
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                        placeholder="имя / фамилия / cookie / tx"
                        className={`${inputClass} w-56`}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Сортировка
                    </label>
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className={inputClass}
                    >
                        <option value="date_time">Дата оплаты</option>
                        <option value="amount">Сумма</option>
                        <option value="id">ID</option>
                        <option value="created_at">Создан</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Порядок
                    </label>
                    <select
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value as "asc" | "desc")}
                        className={inputClass}
                    >
                        <option value="desc">По убыванию</option>
                        <option value="asc">По возрастанию</option>
                    </select>
                </div>
                <button
                    type="submit"
                    className="inline-flex items-center px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
                >
                    Применить
                </button>
            </form>

            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 space-y-4">
                {isLoadingStats && (
                    <div className="text-sm text-gray-500">Загрузка статистики...</div>
                )}
                {isStatsError && (
                    <div className="text-sm text-red-600">
                        Ошибка stats API:{" "}
                        {(statsError as { status?: number })?.status ?? "unknown"}
                    </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="p-3 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                        <div className="text-xs text-green-800 dark:text-green-300">
                            Paid, USD
                        </div>
                        <div className="text-xl font-semibold text-green-700 dark:text-green-300">
                            {formatMoney(paid.total_amount)}
                        </div>
                        <div className="text-[10px] text-green-700/70 dark:text-green-400/70">
                            платежей: {paid.count}
                        </div>
                    </div>
                    <div className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800">
                        <div className="text-xs text-yellow-800 dark:text-yellow-300">
                            Pending
                        </div>
                        <div className="text-xl font-semibold text-yellow-700 dark:text-yellow-300">
                            {formatMoney(pending.total_amount)}
                        </div>
                        <div className="text-[10px] text-yellow-700/70">
                            платежей: {pending.count}
                        </div>
                    </div>
                    <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800">
                        <div className="text-xs text-red-800 dark:text-red-300">
                            Failed
                        </div>
                        <div className="text-xl font-semibold text-red-700 dark:text-red-300">
                            {formatMoney(failed.total_amount)}
                        </div>
                        <div className="text-[10px] text-red-700/70">
                            платежей: {failed.count}
                        </div>
                    </div>
                </div>

                <div>
                    <h2 className="text-lg font-semibold mb-2 dark:text-gray-200">
                        Paid по источнику
                    </h2>
                    <table className="min-w-full text-sm max-w-md">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-gray-700">
                                <th className="text-left py-2 pr-4 dark:text-gray-200">Источник</th>
                                <th className="text-right py-2 pr-4 dark:text-gray-200">Кол-во</th>
                                <th className="text-right py-2 pr-4 dark:text-gray-200">Сумма</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(bySource).map(([key, s]) => (
                                <tr
                                    key={key}
                                    className="border-b border-gray-100 dark:border-gray-800"
                                >
                                    <td className="py-1 pr-4 dark:text-gray-300">
                                        {sourceLabel(key)}
                                    </td>
                                    <td className="py-1 pr-4 text-right dark:text-gray-300">
                                        {s.count}
                                    </td>
                                    <td className="py-1 pr-4 text-right dark:text-gray-300">
                                        {formatMoney(s.total_amount)}
                                    </td>
                                </tr>
                            ))}
                            {Object.keys(bySource).length === 0 && (
                                <tr>
                                    <td colSpan={3} className="py-2 text-gray-500">
                                        Нет paid за период
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <h2 className="text-lg font-semibold mb-4 dark:text-gray-200">
                    Список платежей
                </h2>
                {isLoadingList ? (
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                        Загрузка платежей...
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 dark:border-gray-700">
                                    <th className="text-left py-2 pr-4 dark:text-gray-200">ID</th>
                                    <th className="text-left py-2 pr-4 dark:text-gray-200">
                                        Пользователь
                                    </th>
                                    <th className="text-left py-2 pr-4 dark:text-gray-200">
                                        Cookie
                                    </th>
                                    <th className="text-right py-2 pr-4 dark:text-gray-200">
                                        Сумма
                                    </th>
                                    <th className="text-left py-2 pr-4 dark:text-gray-200">
                                        Карта
                                    </th>
                                    <th className="text-left py-2 pr-4 dark:text-gray-200">
                                        Источник
                                    </th>
                                    <th className="text-left py-2 pr-4 dark:text-gray-200">
                                        Статус
                                    </th>
                                    <th className="text-left py-2 pr-4 dark:text-gray-200">
                                        Transaction
                                    </th>
                                    <th className="text-left py-2 pr-4 dark:text-gray-200">
                                        Дата
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.map((p) => (
                                    <tr
                                        key={p.id}
                                        className="border-b border-gray-100 dark:border-gray-800"
                                    >
                                        <td className="py-2 pr-4 dark:text-gray-300">{p.id}</td>
                                        <td className="py-2 pr-4 dark:text-gray-300">
                                            {[p.user_name, p.user_surname].filter(Boolean).join(" ")
                                                || p.user_label
                                                || p.user_id}
                                        </td>
                                        <td className="py-2 pr-4 font-mono text-xs dark:text-gray-300">
                                            {p.secret_cookie || "—"}
                                        </td>
                                        <td className="py-2 pr-4 text-right font-medium tabular-nums dark:text-gray-200">
                                            {formatMoney(p.amount)}{" "}
                                            {(p.currency || "usd").toUpperCase()}
                                        </td>
                                        <td className="py-2 pr-4 font-mono text-xs dark:text-gray-300">
                                            {p.card_mask || "—"}
                                            {p.card_country ? ` · ${p.card_country}` : ""}
                                        </td>
                                        <td className="py-2 pr-4">
                                            <span
                                                className={`px-2 py-1 rounded text-xs font-medium ${
                                                    p.source === "hub"
                                                        ? "bg-indigo-100 text-indigo-800"
                                                        : "bg-sky-100 text-sky-800"
                                                }`}
                                            >
                                                {sourceLabel(p.source)}
                                            </span>
                                        </td>
                                        <td className="py-2 pr-4">
                                            <span
                                                className={`px-2 py-1 rounded text-xs font-medium ${statusBadge(
                                                    p.status
                                                )}`}
                                            >
                                                {p.status}
                                            </span>
                                        </td>
                                        <td className="py-2 pr-4 font-mono text-xs dark:text-gray-300 max-w-[180px] truncate" title={p.transaction_id}>
                                            {p.transaction_id || "—"}
                                        </td>
                                        <td className="py-2 pr-4 text-xs dark:text-gray-300">
                                            {p.date_time || p.created_at || "—"}
                                        </td>
                                    </tr>
                                ))}
                                {payments.length === 0 && (
                                    <tr>
                                        <td colSpan={9} className="py-4 text-center text-gray-500">
                                            Нет платежей
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {meta && meta.last_page > 1 && (
                    <div className="mt-4 flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">
                            {meta.from}–{meta.to} из {meta.total}
                        </span>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                disabled={page <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40"
                            >
                                ←
                            </button>
                            <span className="px-2 py-1 dark:text-gray-300">
                                {meta.current_page} / {meta.last_page}
                            </span>
                            <button
                                type="button"
                                disabled={page >= meta.last_page}
                                onClick={() => setPage((p) => p + 1)}
                                className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40"
                            >
                                →
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StripeStatsPage;
